import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Iconify, Label, Scrollbar } from '@/components';
import { SkeletonLoadingTable } from '@/components/skeleton';
import { TableHeadCustom, TableNoData } from '@/components/table';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { fDateTime } from '@/utils/formatTime';
import { getScheduledJobRuns } from '../api/scheduled-jobs';
import { ScheduledJob, ScheduledJobRun } from '../types/scheduled-jobs';

type ScheduledJobDetailsDrawerProps = {
  open: boolean;
  job?: ScheduledJob | null;
  focusRuns?: boolean;
  canRun?: boolean;
  onRunJob?: VoidFunction;
  onClose: VoidFunction;
};

const RUNS_TABLE_HEAD = [
  { id: 'status', label: 'Status', align: 'left' },
  { id: 'trigger', label: 'Trigger', align: 'left' },
  { id: 'triggeredFor', label: 'Triggered for', align: 'left' },
  { id: 'startedAt', label: 'Started at', align: 'left' },
  { id: 'finishedAt', label: 'Finished at', align: 'left' },
  { id: 'duration', label: 'Duration', align: 'left' },
  { id: 'error', label: 'Error', align: 'left', minWidth: 180 },
  { id: 'responseStatus', label: 'Response status', align: 'left' },
  {
    id: 'responseBody',
    label: 'Response body preview',
    align: 'left',
    minWidth: 220,
  },
];

const safeDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return fDateTime(value);
};

const getStatusColor = (status?: string | null) => {
  const normalized = status?.toLowerCase() ?? '';

  if (['success', 'succeeded', 'completed', 'ok'].includes(normalized)) {
    return 'success' as const;
  }

  if (['failed', 'failure', 'error'].includes(normalized)) {
    return 'error' as const;
  }

  if (['running', 'pending', 'started'].includes(normalized)) {
    return 'warning' as const;
  }

  return 'default' as const;
};

const formatDuration = (run: ScheduledJobRun) => {
  let ms = run.durationMs;

  if (!ms && run.startedAt && run.finishedAt) {
    const startedAt = new Date(run.startedAt).getTime();
    const finishedAt = new Date(run.finishedAt).getTime();

    if (!Number.isNaN(startedAt) && !Number.isNaN(finishedAt)) {
      ms = Math.max(0, finishedAt - startedAt);
    }
  }

  if (!ms) return '-';
  if (ms < 1000) return `${ms} ms`;

  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

const truncate = (value: string, maxLength: number = 160) =>
  value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;

const buildResponsePreview = (run: ScheduledJobRun) => {
  const value = run.responseBodyPreview ?? run.responseBody;
  if (value === undefined || value === null || value === '') return '-';

  if (typeof value === 'string') return truncate(value);

  try {
    return truncate(JSON.stringify(value));
  } catch {
    return '-';
  }
};

const formatResponseBody = (run?: ScheduledJobRun | null) => {
  const value = run?.responseBody ?? run?.responseBodyPreview;
  if (value === undefined || value === null || value === '') return '-';

  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const DetailItem = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.25, wordBreak: 'break-word' }}>
      {value || '-'}
    </Typography>
  </Box>
);

const RunTableRow = ({
  run,
  onSelect,
}: {
  run: ScheduledJobRun;
  onSelect: (run: ScheduledJobRun) => void;
}) => {
  const statusColor = getStatusColor(run.status);
  const errorText = run.error || '-';
  const responsePreview = buildResponsePreview(run);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(run);
    }
  };

  return (
    <TableRow
      hover
      tabIndex={0}
      onClick={() => onSelect(run)}
      onKeyDown={handleKeyDown}
      sx={{ cursor: 'pointer' }}
    >
      <TableCell>
        <Label color={statusColor}>{run.status || '-'}</Label>
      </TableCell>
      <TableCell>{run.trigger || '-'}</TableCell>
      <TableCell>{run.triggeredFor || '-'}</TableCell>
      <TableCell>{safeDateTime(run.startedAt)}</TableCell>
      <TableCell>{safeDateTime(run.finishedAt)}</TableCell>
      <TableCell>{formatDuration(run)}</TableCell>
      <TableCell sx={{ maxWidth: 240 }}>
        {run.error ? (
          <Tooltip title={run.error}>
            <Typography variant="body2" noWrap>
              {errorText}
            </Typography>
          </Tooltip>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell>{run.responseStatus ?? '-'}</TableCell>
      <TableCell sx={{ maxWidth: 280 }}>
        {responsePreview !== '-' ? (
          <Tooltip title={responsePreview}>
            <Typography
              variant="body2"
              noWrap
              sx={{
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              }}
            >
              {responsePreview}
            </Typography>
          </Tooltip>
        ) : (
          '-'
        )}
      </TableCell>
    </TableRow>
  );
};

export default function ScheduledJobDetailsDrawer({
  open,
  job,
  focusRuns,
  canRun,
  onRunJob,
  onClose,
}: ScheduledJobDetailsDrawerProps) {
  const theme = useTheme();
  const { errorMsg } = useSnackbarMsg();
  const runsRef = useRef<HTMLDivElement | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedRun, setSelectedRun] = useState<ScheduledJobRun | null>(null);

  const { data: runsData, isLoading } = useQuery({
    queryKey: ['scheduledJobRuns', job?.id, page, rowsPerPage],
    queryFn: () =>
      getScheduledJobRuns(job?.id ?? '', {
        page: page + 1,
        limit: rowsPerPage,
      }),
    enabled: open && Boolean(job?.id),
    keepPreviousData: true,
    onError: (error) => {
      errorMsg(error, 'Unable to load scheduled job runs');
    },
  });

  useEffect(() => {
    setPage(0);
    setSelectedRun(null);
  }, [job?.id]);

  useEffect(() => {
    if (!open || !focusRuns) return;

    window.setTimeout(() => {
      runsRef.current?.scrollIntoView({ block: 'start' });
    }, 150);
  }, [focusRuns, open, job?.id]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const runs = runsData?.data ?? [];
  const totalRuns = runsData?.meta?.total ?? runs.length;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: 1, md: 'calc(100vw - 16px)' },
            maxWidth: 'none',
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              {job?.name || 'Scheduled job'}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
              <Label color={job?.enabled ? 'success' : 'default'}>
                {job?.enabled ? 'Enabled' : 'Disabled'}
              </Label>
              {job?.executionType && (
                <Label
                  variant={theme.palette.mode === 'light' ? 'ghost' : 'filled'}
                  color={job.executionType === 'HTTP' ? 'info' : 'secondary'}
                >
                  {job.executionType}
                </Label>
              )}
              {job?.lastStatus && (
                <Label color={getStatusColor(job.lastStatus)}>
                  {job.lastStatus}
                </Label>
              )}
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              color="warning"
              disabled={!job || !canRun}
              onClick={onRunJob}
              startIcon={<Iconify icon="eva:play-circle-fill" />}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Run now
            </Button>
            <IconButton onClick={onClose}>
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Stack>
        </Box>

        <Divider />

        <Scrollbar>
          <Stack spacing={3} sx={{ p: 3 }}>
            <DetailItem label="Description" value={job?.description} />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                },
                gap: 2,
              }}
            >
              <DetailItem label="Cron expression" value={job?.cronExpression} />
              <DetailItem label="Timezone" value={job?.timeZone} />
              <DetailItem
                label="Last run at"
                value={safeDateTime(job?.lastRunAt)}
              />
              <DetailItem label="Timeout ms" value={job?.timeoutMs ?? '-'} />
              <DetailItem label="Lock key" value={job?.lockKey} />
              {job?.executionType === 'HTTP' ? (
                <>
                  <DetailItem label="HTTP method" value={job.httpMethod} />
                  <DetailItem label="Target URL" value={job.targetUrl} />
                </>
              ) : (
                <DetailItem label="Handler key" value={job?.handlerKey} />
              )}
            </Box>

            {job?.lastError && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Last error
                </Typography>
                <Chip
                  variant="outlined"
                  color="error"
                  label={job.lastError}
                  sx={{
                    mt: 0.75,
                    maxWidth: '100%',
                    height: 'auto',
                    py: 0.75,
                    '& .MuiChip-label': {
                      display: 'block',
                      whiteSpace: 'normal',
                    },
                  }}
                />
              </Box>
            )}

            <Box ref={runsRef}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                <Box>
                  <Typography variant="h6">Run history</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Recent executions for this scheduled job
                  </Typography>
                </Box>
              </Stack>

              {isLoading ? (
                <SkeletonLoadingTable />
              ) : (
                <>
                  <Scrollbar>
                    <TableContainer sx={{ minWidth: 1400 }}>
                      <Table size="small">
                        <TableHeadCustom headLabel={RUNS_TABLE_HEAD} />
                        <TableBody>
                          {runs.map((run) => (
                            <RunTableRow
                              key={run.id}
                              run={run}
                              onSelect={setSelectedRun}
                            />
                          ))}

                          <TableNoData
                            isNotFound={!runs.length}
                            title="No runs found"
                          />
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Scrollbar>

                  {totalRuns > rowsPerPage && (
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50]}
                      component="div"
                      count={totalRuns}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  )}
                </>
              )}
            </Box>
          </Stack>
        </Scrollbar>
      </Drawer>

      <Dialog
        fullWidth
        maxWidth="md"
        open={Boolean(selectedRun)}
        onClose={() => setSelectedRun(null)}
      >
        <DialogTitle>Run response body</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              <DetailItem
                label="Status"
                value={selectedRun?.responseStatus ?? selectedRun?.status}
              />
              <DetailItem
                label="Started at"
                value={safeDateTime(selectedRun?.startedAt)}
              />
              <DetailItem
                label="Duration"
                value={selectedRun ? formatDuration(selectedRun) : '-'}
              />
            </Box>

            {selectedRun?.error && (
              <DetailItem label="Error" value={selectedRun.error} />
            )}

            <Box
              component="pre"
              sx={{
                m: 0,
                p: 2,
                borderRadius: 1,
                bgcolor: 'grey.100',
                color: 'text.primary',
                maxHeight: { xs: 360, md: 520 },
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                fontSize: 13,
              }}
            >
              {formatResponseBody(selectedRun)}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setSelectedRun(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
